import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CheckIcon,
  ChevronLeftIcon,
  CloseIcon,
  MicrophoneFilledIcon,
} from "@/components/Icons";
import {
  configureGlobalDictationAsync,
  getGlobalDictationStatusAsync,
  GlobalDictationStatus,
  openGlobalDictationKeyboardSettingsAsync,
  requestGlobalDictationMicrophonePermissionAsync,
  setGlobalDictationEnabledAsync,
  startGlobalDictationFlowSessionAsync,
} from "@/models/useGlobalDictation";
import { config } from "@/controller";
import { useAppStore } from "@/controller";
import { logClientEvent } from "@/telemetry";
import { getPlatform, getPlatformRuntime } from "@/platform";
import { recordFeatureUsed } from "@/models/feature-adoption";
import {
  getIosKeyboardEffect,
  getIosResumeStep,
  getNextDictationStep,
  getPreviousDictationStep,
  hasCompletedDictationTryIt,
  iosDictationSteps,
  type IosDictationStep,
} from "@/dictation-interactions";

// iOS counterpart of GlobalDictationOnboarding (Android). The Android flow
// walks overlay + accessibility permissions; the iOS flow walks the custom
// keyboard setup instead: mic permission → add keyboard in iOS Settings →
// enable Allow Full Access → try it. Steps live-poll native status so they
// check themselves off as the user completes them in Settings.
//
// The "tryit" step is the activation moment (Wispr Flow's "see it appear
// like magic"): we arm the Flow Session recorder ourselves and have the
// user dictate into an in-app field, so their first dictation succeeds here
// instead of failing against a dead session in Messages.
// While the keyboard step is visible we poll status — the user flips the
// switches in the Settings app, so there is no in-app event to listen for.
const KEYBOARD_STEP_POLL_MS = 1500;

type Props = {
  open: boolean;
  onClose: () => void;
};

const GlobalDictationOnboardingIOS = ({ open, onClose }: Props) => {
  const { t } = useTranslation();
  const userToken = useAppStore((state) => state.userToken);

  const [stepKey, setStepKey] = useState<IosDictationStep>("intro");
  const [status, setStatus] = useState<GlobalDictationStatus>();
  const [busy, setBusy] = useState(false);
  const [tryText, setTryText] = useState("");
  const stepKeyRef = useRef(stepKey);
  stepKeyRef.current = stepKey;

  const refreshStatus = useCallback(async () => {
    try {
      const next = await configureGlobalDictationAsync({
        authToken: userToken || "",
        serverUrl: config.apiUrl,
      });
      setStatus(next);
      return next;
    } catch (error) {
      console.error("[GlobalDictation] configure failed", error);
      const fallback = await getGlobalDictationStatusAsync();
      setStatus(fallback);
      return fallback;
    }
  }, [userToken]);

  useEffect(() => {
    if (!open) return;
    setStepKey("intro");
    setTryText("");
    // iOS kills the app when Allow Full Access is toggled in Settings, so a
    // mid-setup user reopens at square one. Resume them on the keyboard step
    // instead of replaying intro/microphone.
    refreshStatus().then((next) => {
      setStepKey(getIosResumeStep(stepKeyRef.current, next));
    });
  }, [open, refreshStatus]);

  useEffect(() => {
    if (!open) return;
    logClientEvent("dictation.onboardingStepViewed", {
      step: stepKey,
      platform: "ios",
    });
    if (stepKey === "done") {
      logClientEvent("dictation.onboardingCompleted");
      recordFeatureUsed("dictation");
    }
  }, [open, stepKey]);

  // Re-check when the user comes back from the Settings app...
  useEffect(() => {
    if (!open) return;
    let resumeListener: { remove: () => Promise<void> } | undefined;
    getPlatformRuntime()
      .app.onResume(refreshStatus)
      .then((listener) => {
        resumeListener = listener;
      });
    return () => {
      resumeListener?.remove();
    };
  }, [open, refreshStatus]);

  // ...and poll while the keyboard step is visible (Split View users never
  // leave the foreground, so resume alone isn't enough).
  useEffect(() => {
    if (!open || stepKey !== "keyboard") return;
    const interval = setInterval(refreshStatus, KEYBOARD_STEP_POLL_MS);
    return () => clearInterval(interval);
  }, [open, stepKey, refreshStatus]);

  // Wispr Flow's install pattern: instructions, not validation. The moment
  // the keyboard shows up as added (the user came back from Settings),
  // advance for them — Full Access is never gated here because iOS only
  // lets us confirm it once the keyboard is opened, which the try-it step
  // does anyway. Transition-guarded so re-entering the step with the
  // keyboard already added doesn't bounce straight back out (back button).
  const sawKeyboardOff = useRef(false);
  useEffect(() => {
    if (!open || stepKey !== "keyboard" || !status) return;
    if (!status.keyboardEnabled) {
      sawKeyboardOff.current = true;
      return;
    }
    if (sawKeyboardOff.current) {
      sawKeyboardOff.current = false;
      setGlobalDictationEnabledAsync(true).catch(() => {});
      setStepKey("tryit");
    }
  }, [open, stepKey, status]);

  // Entering the try-it step arms the app-side recorder so the user's very
  // first dictation works. Re-arm on resume too — flipping to Settings or a
  // long pause could otherwise let the session lapse mid-step.
  useEffect(() => {
    if (!open || stepKey !== "tryit") return;
    const arm = () => {
      startGlobalDictationFlowSessionAsync()
        .then((next) => {
          if (next) setStatus(next);
        })
        .catch(() => {});
    };
    arm();
    let resumeListener: { remove: () => Promise<void> } | undefined;
    getPlatformRuntime()
      .app.onResume(arm)
      .then((listener) => {
        resumeListener = listener;
      });
    return () => {
      resumeListener?.remove();
    };
  }, [open, stepKey]);

  // The magic moment: transcribed text landed in the try-it field. Celebrate
  // briefly, then advance on the user's behalf (Wispr Flow's "Well done!").
  const hasDictated = hasCompletedDictationTryIt(tryText);
  useEffect(() => {
    if (!open || stepKey !== "tryit" || !hasDictated) return;
    logClientEvent("dictation.permissionResult", {
      step: "tryit-dictated",
      platform: "ios",
    });
    const timer = setTimeout(() => setStepKey("done"), 2500);
    return () => clearTimeout(timer);
  }, [open, stepKey, hasDictated]);

  if (!open || getPlatform() !== "ios") return null;

  const stepIndex = iosDictationSteps.indexOf(stepKey);
  const keyboardAdded = Boolean(status?.keyboardEnabled);
  const fullAccessOn = Boolean(status?.fullAccessGranted);
  const microphoneGranted = Boolean(status?.microphoneGranted);

  const goBack = () => {
    setStepKey((current) =>
      getPreviousDictationStep(iosDictationSteps, current),
    );
  };

  const goNext = () => {
    setStepKey((current) => getNextDictationStep(iosDictationSteps, current));
  };

  const handleMicrophoneStep = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const next = await requestGlobalDictationMicrophonePermissionAsync();
      setStatus(next);
      if (next.microphoneGranted) goNext();
    } finally {
      setBusy(false);
    }
  };

  const openKeyboardSettings = () => {
    logClientEvent("dictation.permissionResult", {
      step: "keyboard-settings-opened",
      platform: "ios",
    });
    openGlobalDictationKeyboardSettingsAsync().catch(() => {});
  };

  const finishSetup = async (nextStep: IosDictationStep) => {
    await setGlobalDictationEnabledAsync(true).catch(() => {});
    setStepKey(nextStep);
  };

  const checklistRow = (done: boolean, label: string) => (
    <div className="flex items-center gap-3 rounded-2xl glass-tile px-4 py-3">
      <span
        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
          done
            ? "bg-[var(--accent)] text-[var(--text-inverse)]"
            : "bg-[var(--glass-bg-active)] text-[var(--text-tertiary)]"
        }`}
      >
        {done ? <CheckIcon size={13} strokeWidth={3} /> : null}
      </span>
      <span
        className={`text-sm font-medium ${
          done ? "text-[var(--text-primary)]" : "text-[var(--text-secondary)]"
        }`}
      >
        {label}
      </span>
    </div>
  );

  return (
    <div className="app-page-background fixed inset-0 z-50 flex flex-col bg-[var(--bg-primary)] text-[var(--text-primary)]">
      <header
        className="flex items-center justify-between px-4 pb-2"
        style={{ paddingTop: "calc(env(safe-area-inset-top) + 0.75rem)" }}
      >
        <button
          data-action-id="dictation.setup.backOrClose"
          onClick={stepIndex === 0 ? onClose : goBack}
          className="flex h-10 w-10 items-center justify-center rounded-full glass-tile active:bg-[var(--glass-bg-active)]"
          aria-label={t("globalDictationOnboarding.back", {
            defaultValue: "Back",
          })}
        >
          <ChevronLeftIcon size={20} />
        </button>
        <div className="flex items-center gap-1.5">
          {iosDictationSteps.map((key, index) => (
            <span
              key={key}
              className={`h-1.5 rounded-full transition-all ${
                index === stepIndex
                  ? "w-5 bg-[var(--accent)]"
                  : "w-1.5 bg-[var(--glass-bg-active)]"
              }`}
            />
          ))}
        </div>
        <button
          data-action-id="dictation.setup.backOrClose"
          onClick={onClose}
          className="flex h-10 w-10 items-center justify-center rounded-full glass-tile active:bg-[var(--glass-bg-active)]"
          aria-label={t("globalDictationOnboarding.close", {
            defaultValue: "Close",
          })}
        >
          <CloseIcon size={18} />
        </button>
      </header>

      <main
        className="flex-1 overflow-y-auto px-6"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 1.5rem)" }}
      >
        {stepKey === "intro" && (
          <div className="flex h-full flex-col">
            <div className="flex flex-1 flex-col items-center justify-center text-center">
              <span className="flex h-20 w-20 items-center justify-center rounded-full bg-[var(--accent)] text-[var(--text-inverse)] shadow-2xl shadow-[var(--accent-glow)]">
                <MicrophoneFilledIcon size={36} />
              </span>
              <h1 className="mt-6 text-3xl font-semibold leading-tight">
                {t("globalDictationOnboardingIOS.introTitle", {
                  defaultValue: "Dictate in any app",
                })}
              </h1>
              <p className="mt-3 max-w-[300px] text-base leading-relaxed text-[var(--text-secondary)]">
                {t("globalDictationOnboardingIOS.introBody", {
                  defaultValue:
                    "Add the 1Transcribe keyboard and turn your voice into text anywhere you can type — Messages, Mail, WhatsApp and more.",
                })}
              </p>
            </div>
            <button
              data-action-id="dictation.setup.continue"
              onClick={goNext}
              className="h-14 w-full rounded-full bg-[var(--accent)] text-base font-semibold text-[var(--text-inverse)] active:scale-[0.99] transition-transform"
            >
              {t("globalDictationOnboardingIOS.introCta", {
                defaultValue: "Set up in 2 minutes",
              })}
            </button>
          </div>
        )}

        {stepKey === "microphone" && (
          <div className="flex h-full flex-col">
            <div className="flex-1 pt-6">
              <h1 className="text-2xl font-semibold">
                {t("globalDictationOnboardingIOS.microphoneTitle", {
                  defaultValue: "Allow the microphone",
                })}
              </h1>
              <p className="mt-2 text-sm leading-relaxed text-[var(--text-secondary)]">
                {t("globalDictationOnboardingIOS.microphoneBody", {
                  defaultValue:
                    "The keyboard uses the microphone to hear your dictation. iOS asks for this permission here, in the app.",
                })}
              </p>
              {microphoneGranted && (
                <div className="mt-6">
                  {checklistRow(
                    true,
                    t("globalDictationOnboardingIOS.microphoneGranted", {
                      defaultValue: "Microphone allowed",
                    }),
                  )}
                </div>
              )}
            </div>
            <button
              data-action-id="dictation.setup.continue"
              onClick={microphoneGranted ? goNext : handleMicrophoneStep}
              disabled={busy}
              className="h-14 w-full rounded-full bg-[var(--accent)] text-base font-semibold text-[var(--text-inverse)] active:scale-[0.99] transition-transform disabled:opacity-60"
            >
              {microphoneGranted
                ? t("globalDictationOnboardingIOS.continue", {
                    defaultValue: "Continue",
                  })
                : t("globalDictationOnboardingIOS.allowMicrophone", {
                    defaultValue: "Allow microphone",
                  })}
            </button>
          </div>
        )}

        {stepKey === "keyboard" && (
          <div className="flex h-full flex-col">
            <div className="flex-1 pt-6">
              <h1 className="text-2xl font-semibold">
                {t("globalDictationOnboardingIOS.keyboardTitle", {
                  defaultValue: "Add the keyboard",
                })}
              </h1>
              <p className="mt-2 text-sm leading-relaxed text-[var(--text-secondary)]">
                {t("globalDictationOnboardingIOS.keyboardBodyV2", {
                  defaultValue:
                    "One quick trip to Settings — four taps and you're back.",
                })}
              </p>

              {/* Wispr Flow-style instruction card: tell, don't validate.
                  iOS can't report the Full Access toggle to us anyway; the
                  try-it step is the real verification. */}
              <div className="mt-6 space-y-2 rounded-2xl glass-tile p-4">
                {[
                  t("globalDictationOnboardingIOS.kbStep1", {
                    defaultValue: "Tap Keyboards",
                  }),
                  t("globalDictationOnboardingIOS.kbStep2", {
                    defaultValue: "Turn on 1Transcribe",
                  }),
                  t("globalDictationOnboardingIOS.kbStep3", {
                    defaultValue: "Turn on Allow Full Access",
                  }),
                  t("globalDictationOnboardingIOS.kbStep4", {
                    defaultValue: "Tap Allow, then come back here",
                  }),
                ].map((label, index) => (
                  <div key={index} className="flex items-center gap-3 py-1.5">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--glass-bg-active)] text-xs font-semibold text-[var(--text-secondary)]">
                      {index + 1}
                    </span>
                    <span className="text-sm font-medium text-[var(--text-primary)]">
                      {label}
                    </span>
                  </div>
                ))}
              </div>

              {keyboardAdded && (
                <div className="mt-4">
                  {checklistRow(
                    true,
                    t("globalDictationOnboardingIOS.keyboardAddedChip", {
                      defaultValue: "1Transcribe keyboard added",
                    }),
                  )}
                </div>
              )}

              <p className="mt-4 text-xs leading-relaxed text-[var(--text-tertiary)]">
                {t("globalDictationOnboardingIOS.fullAccessWhy", {
                  defaultValue:
                    "Full Access is required so the keyboard can send your recording to 1Transcribe for transcription. Your keystrokes are never collected.",
                })}
              </p>
            </div>
            <div className="space-y-3">
              {/* Detection is best-effort only (iOS's keyboard list can be
                  stale in both directions around reinstalls), so Continue
                  must never be hostage to it: the user can always
                  self-declare, and the try-it step is the real proof. */}
              <button
                data-action-id="dictation.keyboard.settings.open"
                onClick={
                  getIosKeyboardEffect(keyboardAdded) === "continue"
                    ? () => finishSetup("tryit")
                    : openKeyboardSettings
                }
                className="h-14 w-full rounded-full bg-[var(--accent)] text-base font-semibold text-[var(--text-inverse)] active:scale-[0.99] transition-transform"
              >
                {keyboardAdded
                  ? t("globalDictationOnboardingIOS.continue", {
                      defaultValue: "Continue",
                    })
                  : t("globalDictationOnboardingIOS.openSettings", {
                      defaultValue: "Open Settings",
                    })}
              </button>
              {!keyboardAdded && (
                <button
                  data-action-id="dictation.keyboard.confirmAdded"
                  onClick={() => finishSetup("tryit")}
                  className="h-12 w-full rounded-full glass-tile text-sm font-semibold text-[var(--text-primary)] active:bg-[var(--glass-bg-active)]"
                >
                  {t("globalDictationOnboardingIOS.addedItContinue", {
                    defaultValue: "I've added it — Continue",
                  })}
                </button>
              )}
              <button
                data-action-id="dictation.setup.skip"
                onClick={() => finishSetup("done")}
                className="h-10 w-full text-center text-sm font-medium text-[var(--text-tertiary)] active:opacity-70"
              >
                {t("globalDictationOnboardingIOS.skipForNow", {
                  defaultValue: "I'll finish this later",
                })}
              </button>
            </div>
          </div>
        )}

        {stepKey === "tryit" && (
          <div className="flex h-full flex-col">
            <div className="flex-1 pt-6">
              <h1 className="text-2xl font-semibold">
                {t("globalDictationOnboardingIOS.tryTitle", {
                  defaultValue: "Your turn — say anything",
                })}
              </h1>
              <p className="mt-2 text-sm leading-relaxed text-[var(--text-secondary)]">
                {t("globalDictationOnboardingIOS.tryBody", {
                  defaultValue:
                    "Tap the field below, hold the 🌐 globe key and choose 1Transcribe, then tap the mic and speak. Your words appear like magic.",
                })}
              </p>

              <textarea
                data-action-id="dictation.try.change"
                value={tryText}
                onChange={(event) => setTryText(event.target.value)}
                rows={3}
                placeholder={t("globalDictationOnboardingIOS.tryPlaceholder", {
                  defaultValue: "Say something!",
                })}
                className="mt-6 w-full resize-none rounded-2xl glass-tile px-4 py-4 text-base text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] outline-none focus:ring-2 focus:ring-[var(--accent)]"
              />

              {hasDictated ? (
                <div className="mt-4">
                  {checklistRow(
                    true,
                    t("globalDictationOnboardingIOS.trySuccess", {
                      defaultValue: "Well done! That's all there is to it.",
                    }),
                  )}
                </div>
              ) : (
                <p className="mt-4 text-xs leading-relaxed text-[var(--text-tertiary)]">
                  {t("globalDictationOnboardingIOS.tryWhisperHint", {
                    defaultValue:
                      "Can't speak out loud right now? Whispering works too 😉",
                  })}
                </p>
              )}

              {!hasDictated && !fullAccessOn && (
                <p className="mt-3 text-xs leading-relaxed text-[var(--text-tertiary)]">
                  {t("globalDictationOnboardingIOS.tryFullAccessNote", {
                    defaultValue:
                      "If the keyboard shows “Allow Full Access to dictate”, flip it on in Settings → Keyboards → 1Transcribe and come back here.",
                  })}
                </p>
              )}
            </div>
            <button
              data-action-id="dictation.setup.continue"
              onClick={goNext}
              className={`h-14 w-full rounded-full text-base font-semibold transition-transform active:scale-[0.99] ${
                hasDictated
                  ? "bg-[var(--accent)] text-[var(--text-inverse)]"
                  : "glass-tile text-[var(--text-primary)] active:bg-[var(--glass-bg-active)]"
              }`}
            >
              {hasDictated
                ? t("globalDictationOnboardingIOS.continue", {
                    defaultValue: "Continue",
                  })
                : t("globalDictationOnboardingIOS.trySkip", {
                    defaultValue: "Skip the practice run",
                  })}
            </button>
          </div>
        )}

        {stepKey === "done" && (
          <div className="flex h-full flex-col">
            <div className="flex flex-1 flex-col items-center justify-center text-center">
              <span className="flex h-20 w-20 items-center justify-center rounded-full bg-[var(--accent)] text-[var(--text-inverse)]">
                <CheckIcon size={36} strokeWidth={2.5} />
              </span>
              <h1 className="mt-6 text-3xl font-semibold leading-tight">
                {t("globalDictationOnboardingIOS.doneTitle", {
                  defaultValue: "You're ready to dictate",
                })}
              </h1>
              <p className="mt-3 max-w-[300px] text-base leading-relaxed text-[var(--text-secondary)]">
                {t("globalDictationOnboardingIOS.doneBodyV2", {
                  defaultValue:
                    "In any app, hold the 🌐 globe key and pick 1Transcribe. If the mic needs waking up, tapping it brings you here for a second — then swipe back and talk.",
                })}
              </p>
            </div>
            <button
              data-action-id="dictation.setup.continue"
              onClick={onClose}
              className="h-14 w-full rounded-full bg-[var(--accent)] text-base font-semibold text-[var(--text-inverse)] active:scale-[0.99] transition-transform"
            >
              {t("globalDictationOnboardingIOS.doneCta", {
                defaultValue: "Done",
              })}
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default GlobalDictationOnboardingIOS;
